import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import { Badge } from '../ui/badge';
import { Item, ItemContent, ItemHeader } from '../ui/item';
import { type DetailSection, getNote, getReason, joinFormattedConcepts } from './serviceRequestUtils';

export type ServiceRequestDetailProps = {
  serviceRequest: WithId<ServiceRequest>;
};

const getOccurrence = (sr: WithId<ServiceRequest>): string | null => {
  if (sr.occurrenceDateTime) return formatDateTime(sr.occurrenceDateTime);
  const start = sr.occurrencePeriod?.start;
  const end = sr.occurrencePeriod?.end;
  if (start && end) return `${formatDateTime(start)} – ${formatDateTime(end)}`;
  return start ? formatDateTime(start) : end ? formatDateTime(end) : null;
};

const getIdentifiers = (sr: WithId<ServiceRequest>): string | null =>
  sr.identifier
    ?.map((id) => id.value)
    .filter((value): value is string => Boolean(value))
    .join(', ') || null;

const getSections = (sr: WithId<ServiceRequest>, t: TFunction<'records'>): DetailSection[] =>
  (
    [
      { title: t('authoredOn'), value: sr.authoredOn ? formatDateTime(sr.authoredOn) : null },
      { title: t('scheduledFor'), value: getOccurrence(sr) },
      { title: t('requester'), value: sr.requester?.display ?? null },
      {
        title: t('performer'),
        value:
          sr.performer
            ?.map((p) => p.display)
            .filter((display): display is string => Boolean(display))
            .join(', ') || null,
      },
      { title: t('bodySite'), value: joinFormattedConcepts(sr.bodySite) },
      { title: t('reason'), value: getReason(sr) },
      { title: t('orderNumbers'), value: getIdentifiers(sr) },
      { title: t('patientInstruction'), value: sr.patientInstruction ?? null, preserveWhitespace: true },
      { title: t('notes'), value: getNote(sr), preserveWhitespace: true },
    ] as { title: string; value: string | null; preserveWhitespace?: boolean }[]
  ).filter((s): s is DetailSection => s.value !== null);

type BadgeConfig = { kind: 'status' | 'intent' | 'category' | 'priority'; value: string };

const getBadges = (sr: WithId<ServiceRequest>, t: TFunction<'records'>): BadgeConfig[] =>
  (
    [
      { kind: 'status', value: sr.status || t('unknown') },
      { kind: 'intent', value: sr.intent ?? null },
      { kind: 'category', value: joinFormattedConcepts(sr.category) },
      { kind: 'priority', value: sr.priority && sr.priority !== 'routine' ? sr.priority : null },
    ] as { kind: BadgeConfig['kind']; value: string | null }[]
  ).filter((b): b is BadgeConfig => b.value !== null);

export const ServiceRequestDetail = ({ serviceRequest }: ServiceRequestDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const name = formatCodeableConcept(serviceRequest.code) || t('unknownServiceRequest');
  const badges = getBadges(serviceRequest, t);
  const sections = getSections(serviceRequest, t);

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      <div className="mb-6">
        <h1 className="text-2xl font-medium">{name}</h1>
        <div className="mt-2 flex items-center gap-2">
          {badges.map(({ kind, value }) => (
            <Badge key={kind} variant="outline">
              {value}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {sections.map(({ title, value, preserveWhitespace }) => (
          <Item key={title}>
            <ItemHeader>
              <ItemContent>
                <h2 className="text-lg font-semibold">{title}</h2>
                <p className={preserveWhitespace ? 'mt-2 whitespace-pre-wrap' : 'mt-2'}>{value}</p>
              </ItemContent>
            </ItemHeader>
          </Item>
        ))}
      </div>
    </div>
  );
};
